import {useMemo} from 'react';
import {
  Breakpoints,
  Ratio,
  Widths,
  PrimaryResizeSide,
  Breakpoint,
  ColumnsBreakpoint,
} from './types';

export const getRatio = (ratio: Ratio) => {
  const [widthRatio, heightRatio] = ratio.split(':');
  return {widthRatio, heightRatio};
};

export const getHeight = ({ratio, width}: {ratio: Ratio; width: number}) => {
  const {widthRatio, heightRatio} = getRatio(ratio);
  return (
    (width / parseFloat(widthRatio ?? '1')) * parseFloat(heightRatio ?? '1')
  );
};

const extractParamsAndBase = (src: string) => {
  const [urlBase = src, urlParams = ''] = src.split('?');
  const newUrlParams = new URLSearchParams(urlParams);

  return {urlBase, newUrlParams};
};

export const isColumnsBreakpoint = (
  breakpoint: Breakpoint,
): breakpoint is ColumnsBreakpoint =>
  typeof (breakpoint as ColumnsBreakpoint).columns !== 'undefined';

export const useSrc = ({
  breakpoints,
  ratio,
  src,
  widths,
  primaryResizeSide,
}: {
  breakpoints: Breakpoints;
  ratio: Ratio;
  src: string;
  widths: Widths;
  primaryResizeSide?: PrimaryResizeSide;
}) => {
  // set sensible default src
  const defaultSrc = useMemo(() => {
    const {urlBase, newUrlParams} = extractParamsAndBase(src);
    if (!(newUrlParams.has('width') || newUrlParams.has('height'))) {
      if (primaryResizeSide !== 'width') {
        newUrlParams.set('height', '480');
      }
      if (primaryResizeSide !== 'height') {
        newUrlParams.set('width', '360');
      }
    }
    return `${urlBase}?${newUrlParams.toString()}`;
  }, [src, primaryResizeSide]);

  const srcSet = useMemo(() => {
    // generating the `newUrlParams` is a bit expensive as it generates garbage, therefore we only define it once. This is safe as it always overrides the same values but never reads
    const {urlBase, newUrlParams} = extractParamsAndBase(src);
    return widths
      .map(imageWidth => {
        const imageHeight = getHeight({ratio, width: imageWidth});

        if (primaryResizeSide !== 'width') {
          newUrlParams.set('height', `${Math.ceil(imageHeight)}`);
        }
        if (primaryResizeSide !== 'height') {
          newUrlParams.set('width', `${Math.ceil(imageWidth)}`);
        }

        return `${urlBase}?${newUrlParams.toString()} ${imageWidth}w`;
      })
      .join(', ');
  }, [ratio, widths, src, primaryResizeSide]);

  const sizes = useMemo(
    () => breakpoints.map(breakpointToSize).join(', '),
    [breakpoints],
  );

  return {sizes, srcSet, defaultSrc};
};

const breakpointToSize = (breakpoint: Breakpoint) => {
  const value = isColumnsBreakpoint(breakpoint)
    ? `calc(100vw / ${breakpoint.columns})`
    : breakpoint.width;
  return breakpoint.breakpoint
    ? `${cleanBreakpoint(breakpoint.breakpoint)} ${value}`
    : value;
};

const cleanBreakpoint = (breakpoint: string) => {
  if (breakpoint.includes('only screen and')) {
    return breakpoint.replace('only screen and', '').trim();
  }
  return breakpoint;
};
